// §4.7 — THE ORDER LIST, /sales/orders (Module 09, slice P3-C-1).
//
// This slice ships the order READ surface because §3.5's acceptance
// transaction CREATES orders: a customer taps Accept, an order exists, and an
// order nobody can open is a document the product has lost. The WRITE half a
// full §4.7 needs arrived slice by slice on the detail screen: shipments with
// P3-D PR-D3 (D-286), the proforma and the forced status with PR-D4 (D-288).
// Re-promise landed there with P3-J J-3 (D-469). The DIRECT-ORDER CREATE landed with P3-D PR-D1
// (D-283): the "New order" button in the page head opens the §4.4 composer in
// order mode (`/sales/orders/new`), gated on `sales.order.write`.
//
// BORN SORTED, STICKY AND KEYBOARD-NAVIGABLE (wave 1a/1b/1d). NOT selectable:
// §4.7 declares no bulk verb, and a selection column with no bar behind it is
// a control that does nothing. The wave is opt-in per screen for exactly this
// reason — "born with wave 1" means born with the affordances the screen has a
// use for, not with all four regardless.
//
// The promise-date column shows a typed or imported date as it would a CTP
// date (D-349 ruling 1). It has no sort token, because the contract has none.
// From P3-J J-3 (spec 09 §4.7, D-466) a row the server flags `at_risk` reads
// its date red with `at_risk_reason` in words beside it: a promise broken, a
// promise at risk, or a date passed on a line not yet ready.
//
// THE FILTER ROW IS §4.7's, NOT A THIRD OF IT. §4.7 declares "status,
// customer, facility, promise-date-at-risk toggle" and the wire has carried
// `customer_id` and `facility_id` since this screen's first commit — but the
// bar shipped with STATUS alone, while the screen's stated pending named only
// the at-risk toggle. Two of the four were missing with nothing saying so,
// which is the hidden-gap shape D-033 exists to stop: a pending that under-
// states itself reads as completeness to the next person. Customer and
// facility landed on 2026-09-10, and the at-risk toggle with P3-J J-3, once
// module 03's promise watch gave the flag something to say. The toggle is an
// address filter only (`?at_risk=1`), like `import_job`: the saved-view
// config has no slot for it.
//
// The customer filter is the QUOTE BOOK's shape, deliberately: a search box
// with a picker over the matches (a tenant with 800 customers cannot be a
// Select, and offering the first page of them would silently filter the
// filter), writing the same `?customer=` a link sets, so the 360's order
// panel and the duplicate-PO notice deep-link into it as the quote panel
// deep-links into the book. The facility filter is module 06's chip rule: a
// single-facility tenant is offered NO control rather than a one-option
// select, unless the address already holds a facility, which must stay
// visible to be cleared.
//
// THE ADDRESS IS THE WHOLE STATE (PR-E4, D-305 rulings 3, 4 and 7; spec 09
// §4.7's PR-E4 annotation). `status` and `facility` live in the URL beside
// `customer`, `order` and `view` (screens/search-params.ts), `customerTouched`
// is retired as on the quote book (quotes.tsx says why), and every filter and
// sort write goes through the saved-views controller's `write`. The Total
// column declares `presence`, and D-301's two fallback arms hand the money
// order's drop to the controller, which keeps `view` and every filter. This
// is the screen M16-S18 walks the fence on, because planner, finance and qc
// all open it: a planner who applies finance's shared view sorted by
// `-total_minor` sees rows with no Total column, no chooser entry and no
// caret, sends no `order`, and sees the view applied and unmodified.
//
// EXPORT CSV AND THE CUSTOMER PO (P3-D PR-D5, D-289 rulings 1, 3, 5). The page
// head's Export walks THIS list's read with the filters and sort on screen and
// saves every matching row (components/export-csv.tsx; the columns are
// lib/exports.ts's). The Customer PO column exists because the create's
// duplicate-PO warning points here: the order detail links to this list
// filtered to the customer, and the reader needs to see which order carries
// the same PO. It has no sort control — the wire offers no PO sort.
//
// Import orders (spec 09 §4.19, D-359): `?import_job=` narrows the list to the
// orders one import loaded, and a 403 on it reads as a sentence with a way back.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesFilterBar } from "../components/sales-filter-bar";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { client } from "../../../../lib/api";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { bookPager, useBookPages, useBookWindow } from "../hooks/book-pages";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf, unwrap } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useImport } from "../../../onboarding/hooks/onboarding-queries";
import { useOnboardingPermissions } from "../../../onboarding/hooks/permissions";
import { useSavedViews, ViewSwitcher, ViewsRegion } from "../../views";
import { orderFilterLabels } from "../lib/filter-labels";
import { columnPresent, formatMinor } from "../lib/money";
import {
  addressCarriesRefusedValueOrder,
  isValueOrderRefusal,
  isValueOrderToken,
  valueSortVerdict,
} from "../lib/value-order";
import { ordersCsv, ordersFilters } from "../lib/exports";
import { peekCode } from "../../../../components/peek-address";
import { SalesPeek, useSalesPeek } from "../components/peek/sales-peek";
import { creditChipVariant, orderStatusVariant } from "../lib/chips";
import {
  SalesExportButton,
  SalesExportNotes,
  SalesExportSlot,
  useSalesExport,
} from "../components/export-csv";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { useCustomer360, useCustomers, useOwnerIndex } from "../hooks/sales-queries";
import { useMemberRefAsk, useReadsHeldOffline } from "../lib/reads";
import { useRefNamesSettled } from "../../../../lib/ref-names";
import {
  fetchOrdersPage,
  useOrders,
  type OrdersQuery,
  type SalesOrder,
} from "../hooks/quote-queries";
import {
  compactSearch,
  isOrdersOrderToken,
  ORDER_STATUSES,
  ORDERS_DEFAULT_ORDER,
  validateSalesOrdersSearch,
  type OrdersOrderToken,
  type SalesOrdersSearch,
} from "./search-params";

type OrdersOrderKey = OrdersOrderToken extends `-${infer Base}` ? Base : OrdersOrderToken;

// The order and credit chip variants moved to `lib/chips.ts` (E2-1: the side
// peek paints the same chips) and stay exported here under their old names,
// which the order detail and the inbox import.
export { creditChipVariant, orderStatusVariant } from "../lib/chips";

/**
 * Active facilities — reference data for §4.7's facility filter AND, since
 * P3-D PR-D1 (D-283), the §4.4 composer's order-mode facility picker
 * (`fulfilling_facility_id`).
 *
 * It lived screen-local while it had exactly one consumer, with a note that it
 * would move into `hooks/sales-queries.ts` the day a second sales screen asked
 * for facilities. That day came with the order composer, and the hook is
 * EXPORTED from here rather than moved: `hooks/sales-queries.ts` is P3-A's
 * stable foundations file read by five landed screens, and this slice was
 * built alongside other agents writing in the same tree — a new export from
 * this file cannot collide with an edit to theirs. Every office area already
 * owns its own `useFacilities` over this route (catalogue's, production's and
 * inventory's are three separate hooks, each area's file saying so). Cached
 * like the other areas' — the org tree barely changes within a session — and
 * never retried, because a control that cannot be populated must not hold a
 * list read (or a composer) hostage.
 */
export function useSalesFacilities(enabled = true) {
  return useQuery({
    queryKey: ["sales", "ref", "facilities"] as const,
    // `enabled` exists for the composer's QUOTE mode, which mounts the same
    // component and has no facility control: a read nothing renders is a
    // request nothing needed.
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities", { signal, params: { query: { status: "active" } } }),
        "facilities",
      ),
  });
}

export function SalesOrdersScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const onboarding = useOnboardingPermissions();
  // The side peek (spec 09 §4.15, D-314): a row's Peek button opens the
  // record beside the book, and a row click still opens its page.
  const peek = useSalesPeek();
  const owners = useOwnerIndex(perms.isAdmin);
  // A member directory the browser holds offline stays pending for the whole
  // cut, so the owner reads as its short id until it answers, the way the
  // 360 and the peek read it (#293).
  const directoryOffline = useReadsHeldOffline();
  const ownersResolving = owners.isPending && !directoryOffline;
  const memberRef = useMemberRefAsk(ownersResolving);

  const [customerText, setCustomerText] = useState("");
  const customerQ = useDebouncedValue(customerText.trim(), 250);

  const facilities = useSalesFacilities();
  const facilityItems = facilities.data?.items ?? [];
  const customerMatches = useCustomers({ q: customerQ }, undefined, customerQ.length > 1);

  // THE ADDRESS. D-301: validated with the reader's verdict on the money sort,
  // from the role mirror. The validator drops `±total_minor` only when the
  // mirror KNOWS the reader is not sent totals (lib/value-order.ts), so a
  // custom role that holds the grant keeps its sort and the server decides the
  // rest. The same validator judges a saved view's stored order at apply.
  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const valueSort = valueSortVerdict(perms);
  const validate = useMemo(
    () => (raw: Record<string, unknown>) => validateSalesOrdersSearch(raw, valueSort),
    [valueSort],
  );
  const search = useMemo(() => validate(rawSearch), [rawSearch, validate]);
  const setSearch = (next: SalesOrdersSearch) => {
    void navigate({ to: "/sales/orders", search: compactSearch(next), replace: true });
  };
  const order = search.order ?? ORDERS_DEFAULT_ORDER;
  const customerId = search.customer;

  /** The name behind `?customer=<id>`, resolved through the customer's own
   * read rather than from a loaded row: `priorRows` survives one render past a
   * filter change, so a row-derived name could label the chip with the
   * customer the reader just left. Same query key as the 360's read, so
   * arriving from that screen costs no request, and it fires only while a
   * customer filter is in force. */
  const filteredCustomer = useCustomer360(customerId ?? "");
  const filteredCustomerName = filteredCustomer.data?.customer.display_name;

  /** The file behind `?import_job=`, for the chip and the export's line. The
   * reader the filter answers reads the job through the same door. */
  const importJob = useImport(search.import_job ?? "");
  const mayImportOrders = onboarding.canImport("open_sales_orders");

  const query: OrdersQuery = useMemo(
    () => ({
      ...(search.status !== undefined ? { status: search.status } : {}),
      ...(search.customer !== undefined ? { customer_id: search.customer } : {}),
      ...(search.facility !== undefined ? { facility_id: search.facility } : {}),
      ...(search.order !== undefined ? { order: search.order } : {}),
      ...(search.import_job !== undefined ? { imported_from_job_id: search.import_job } : {}),
      ...(search.at_risk !== undefined ? { at_risk: "true" as const } : {}),
    }),
    [search],
  );
  const queryKey = JSON.stringify(query);

  /** Is a FILTER narrowing this list — not a sort? The two empty states ask
   * for different acts and the sort is not a filter: a sorted, unfiltered
   * empty book is still "no orders yet", and telling that reader to widen
   * their filters would name controls they never touched. */
  const isFiltered =
    search.status !== undefined ||
    search.customer !== undefined ||
    search.facility !== undefined ||
    search.import_job !== undefined ||
    search.at_risk !== undefined;
  const isFilteredBeyondRisk =
    search.status !== undefined ||
    search.customer !== undefined ||
    search.facility !== undefined ||
    search.import_job !== undefined;

  const refused = isSalesReadRefused(perms, perms.has("sales.read"));
  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the query does.
  const pages = useBookPages(queryKey);
  const cursor = pages.cursor;
  // D-301: an address carrying a money order waits for the role feed rather
  // than sending a request the validator may be about to drop.
  const holdForFeed =
    search.order !== undefined && isValueOrderToken(search.order) && valueSort === "pending";

  // THE ROWS THE CONTROLLER JUDGES BY: read through a passive observer of the
  // list's own key before the controller runs, because the controller gates
  // the live read below (the quote book states the reasoning). Cache-
  // synchronous fold, the quote book's shape.
  //
  // THE REVEAL GATE (R11, PR-E4 wave 4): the controller's verdict, carried in
  // state and starting closed, as on the work-order list. While it is closed
  // the observer's page, the live page and the settle read as absent, since on
  // `?view=<id>` alone the list key is the bare list's until the view applies,
  // and a bare page already in the cache would be revealed and then replaced.
  const [listGate, setListGate] = useState(false);
  const shown = useOrders(query, cursor, false);
  const shownData = listGate ? shown.data : undefined;
  const rows = useMemo(() => shownData?.items ?? [], [shownData]);
  const turning = listGate && shown.isPlaceholderData;
  const pageView = useBookWindow(pages, {
    items: rows,
    nextCursor: shownData?.next_cursor,
    turning,
    settled: shownData !== undefined && !shown.isFetching,
  });

  const columns: DataTableColumn<SalesOrder, OrdersOrderKey>[] = [
    { key: "order_no", kind: "id", header: t("sales.orders.colNo"), id: true, sortKey: "order_no" },
    {
      key: "customer",
      phonePriority: 1,
      header: t("sales.orders.colCustomer"),
      // D-560 ruling 2: a long name keeps to two lines and says the rest in
      // its title, so fifty rows stay inside the page's height budget.
      render: (row) => (
        <span className="sales-cell-clamp" title={row.customer_display_name}>
          {row.customer_display_name}
        </span>
      ),
    },
    {
      // The customer's own PO number (§2.16) — NULL renders nothing.
      key: "customer_po_ref",
      header: t("sales.orders.colPo"),
      render: (row) =>
        row.customer_po_ref === null ? null : (
          <span
            className="sales-cell-clamp"
            title={row.customer_po_ref}
            data-testid="sales-order-po"
          >
            {row.customer_po_ref}
          </span>
        ),
    },
    {
      key: "status",
      kind: "status",
      header: t("sales.orders.colStatus"),
      render: (row) => (
        <span className="sales-status-cell">
          <StatusChip
            variant={orderStatusVariant(row.status)}
            data-testid="sales-order-status"
            data-status={row.status}
          >
            {t(`sales.orders.status.${row.status}`)}
          </StatusChip>
          {/* §4.7's "status chip WITH timestamp" — the chip alone says what,
              the stamp says when, and "confirmed" with no date is the fact a
              manager most often needs the date for. */}
          {row.status_at !== null ? (
            <span className="sales-status-at num">
              {formatTimestamp(row.status_at, i18n.language)}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "credit_check_status",
      kind: "status",
      header: t("sales.orders.colCredit"),
      render: (row) =>
        // A passing credit check is the normal case and needs no chip: a
        // column where every row shouts "OK" trains the reader to stop
        // looking, which is the opposite of what the exception needs.
        row.credit_check_status === "passed" ? null : (
          <StatusChip
            variant={creditChipVariant(row.credit_check_status)}
            data-testid="sales-order-credit"
            data-credit={row.credit_check_status}
          >
            {t(`sales.orders.credit.${row.credit_check_status}`)}
          </StatusChip>
        ),
    },
    {
      // D-239 ruling 2 + D-301, as the column's `presence` (design-system
      // §3.3, D-305 ruling 8): the Total column, and with it the money sort's
      // caret and its chooser entry, exists only when a row on the page
      // carries the key. The PAYLOAD decides, not the role mirror:
      // `total_minor` is non-nullable, so every row sent to a holder of
      // `sales.read_value` carries it and no row sent to anyone else does. A
      // planner is offered no sort by a column they are never sent. `sortKey`
      // is how the saved-views controller finds this column for a stored
      // `±total_minor` (spec 16 §3.12 item 7's presence arm).
      key: "total_minor",
      kind: "money",
      phonePriority: 2,
      header: t("sales.orders.colTotal"),
      numeric: true,
      sortKey: "total_minor",
      sortFirst: "desc",
      presence: (loaded) => columnPresent(loaded, (row) => row.total_minor),
      // §7 OMISSION — absent key renders NOTHING.
      render: (row) =>
        row.total_minor === undefined ? null : (
          <span className="num">{formatMinor(row.total_minor, row.currency, i18n.language)}</span>
        ),
    },
    {
      key: "owner_user_id",
      header: t("sales.orders.colOwner"),
      render: (row) => (
        <RefLabel
          value={owners.byId.get(row.owner_user_id)}
          id={row.owner_user_id}
          pending={ownersResolving}
          {...memberRef}
        />
      ),
    },
    {
      key: "promise_date",
      kind: "date",
      phonePriority: 3,
      header: t("sales.orders.colPromise"),
      // A row at risk (P3-J J-3, spec 09 §4.7) reads its date red with the
      // worst reason beside it; the server's rule decides, never this screen.
      render: (row) => {
        const reason = row.at_risk_reason ?? null;
        if (row.promise_date === null && reason === null) return null;
        return (
          <span
            className="sales-order-promise-cell"
            data-at-risk={reason !== null ? "true" : undefined}
            data-at-risk-reason={reason ?? undefined}
          >
            {row.promise_date !== null ? (
              <span
                className={reason !== null ? "num sales-valid-urgent" : "num"}
                data-testid="sales-order-promise"
              >
                {formatBusinessDate(row.promise_date, i18n.language)}
              </span>
            ) : null}
            {reason !== null ? (
              <StatusChip
                variant={reason === "promise_broken" ? "bad" : "warn"}
                dot={false}
                data-testid="sales-order-at-risk"
                data-reason={reason}
              >
                {t(`sales.orders.atRiskReason.${reason}`)}
              </StatusChip>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "confirmed_at",
      kind: "date",
      header: t("sales.orders.colConfirmed"),
      age: true,
      sortKey: "confirmed_at",
      sortFirst: "desc",
      render: (row) =>
        row.confirmed_at === null ? null : formatTimestamp(row.confirmed_at, i18n.language),
    },
  ];

  const views = useSavedViews<SalesOrdersSearch, SalesOrder, OrdersOrderKey>({
    screenKey: "sales.orders",
    search,
    setSearch,
    validate,
    columns,
    rows,
    rowsLoading: !listGate || shown.isFetching,
    // A refused stored filter is named in this bar's own words (V5).
    filterLabels: orderFilterLabels(t),
  });
  if (listGate !== views.listEnabled) setListGate(views.listEnabled);

  const live = useOrders(query, cursor, !refused && !holdForFeed && listGate);
  // The live read as the screen sees it: nothing while the gate is closed.
  const page = {
    data: listGate ? live.data : undefined,
    error: listGate ? live.error : null,
    isPending: !listGate || live.isPending,
    isFetching: listGate && live.isFetching,
    fetchStatus: listGate ? live.fetchStatus : "idle",
    refetch: live.refetch,
  };

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  // D-301: the server refused the money order (a reader the mirror could not
  // judge, such as a custom role without the grant). The sort goes from the
  // address and the list is asked again without it; no error card, because
  // the rows were never refused.
  // Only while the ADDRESS carries a money order: that is the one thing the
  // fallback can take away. The same refusal under any other order is not a
  // refused sort, so it keeps its error card and the address is left alone
  // (rewriting it would only drop the reader's own sort and ask again).
  const valueOrderRefused =
    search.order !== undefined &&
    isValueOrderToken(search.order) &&
    isValueOrderRefusal(page.error);

  // The import filter refused: the job is not one this reader's import door
  // answers (spec 09 §5). Only while the address carries it and no money order
  // explains the 403 instead.
  const importRefused =
    search.import_job !== undefined &&
    !valueOrderRefused &&
    page.error instanceof RequestError &&
    page.error.httpStatus === 403;

  // D-301 round 7: URL validation dropped a money order for a reader the
  // mirror KNOWS is not sent totals, so the list never asked for it. The
  // token still sits in the address. The effect below takes it out, as it
  // does after the server's refusal, so both readers end on one address.
  const valueOrderDropped = addressCarriesRefusedValueOrder(rawSearch, valueSort);

  // Both arms hand the drop to the saved-views controller, which removes
  // `order` alone and keeps `view` and every filter, replacing the history
  // entry (D-305 ruling 7). Keyed on the two arms alone: the controller's
  // functions are new on every render, and the arm is what decides.
  const dropRef = useRef(views.dropRefusedOrder);
  dropRef.current = views.dropRefusedOrder;
  const refusedToken = search.order;
  const droppedToken = typeof rawSearch["order"] === "string" ? rawSearch["order"] : undefined;
  useEffect(() => {
    if (valueOrderRefused) dropRef.current(refusedToken);
    else if (valueOrderDropped) dropRef.current(droppedToken);
  }, [valueOrderRefused, valueOrderDropped]);

  /** Typing a NEW customer search abandons the filter in force, and the box
   * empties when anything but the picker moves the customer (the quote book
   * states both rules). */
  const pickerWrote = useRef(customerId);
  useEffect(() => {
    if (customerId === pickerWrote.current) return;
    pickerWrote.current = customerId;
    setCustomerText("");
  }, [customerId]);
  const writeCustomer = (next: string | undefined) => {
    pickerWrote.current = next;
    views.write({ ...search, customer: next });
  };

  /** Widen back to every customer: the search box empties with it, and the
   * param leaves the ADDRESS, which is the filter's one home. */
  const clearCustomerFilter = () => {
    setCustomerText("");
    writeCustomer(undefined);
  };

  /** Widen back to every order: the import filter leaves the address. */
  const clearImportFilter = () => {
    views.write({ ...search, import_job: undefined });
  };

  const setOrder = (next: string) => {
    if (!isOrdersOrderToken(next)) return;
    views.write({ ...search, order: next === ORDERS_DEFAULT_ORDER ? undefined : next });
  };

  // The export walks the SAME read with the SAME filters, plus the EFFECTIVE
  // sort token (the default one spelled out), so the file's order is the one
  // its provenance row names. Filters are named the way the bar names them.
  //
  // The customer filter's NAME, for the file: the chip's own read, or else a
  // loaded row OF THAT CUSTOMER (matched by id, so a row left over from the
  // previous filter cannot lend its name). A rep reading an order they own on
  // a customer no longer assigned to them has no 360 read to lean on, and the
  // rows still name the customer. Until one of the two answers, the button
  // waits: line 2 never prints the uuid where the chip prints a name.
  const facilityName = facilityItems.find((facility) => facility.id === search.facility)?.name;
  const exportCustomerName =
    filteredCustomerName ??
    rows.find((row) => row.customer_id === customerId)?.customer_display_name;
  const exporter = useSalesExport<SalesOrder>({
    list: "orders",
    resetKey: queryKey,
    fetchPage: (next, limit, signal) => fetchOrdersPage({ ...query, order, limit }, next, signal),
    serialize: ordersCsv,
    filters: ordersFilters(t, {
      status: search.status ?? "",
      customer: customerId !== undefined ? (exportCustomerName ?? customerId) : undefined,
      facility: search.facility !== undefined ? (facilityName ?? search.facility) : undefined,
      importFile:
        search.import_job !== undefined
          ? (importJob.data?.file_name ?? search.import_job)
          : undefined,
      atRisk: search.at_risk !== undefined,
    }),
    order,
    namesPending: customerId !== undefined && exportCustomerName === undefined,
  });

  // The region is revealed once the views read and the first page have both
  // settled (design-system §5, D-305 ruling 21). A refused reader has no list
  // to wait for, and a paused read is offline, which the region must show
  // rather than wait on forever.
  const paused = page.fetchStatus === "paused";
  const listSettled = page.data !== undefined || page.error !== null;
  // ...and once the owners the directory cannot name have been named by the
  // names read: the resolving marker is narrower than a name, so an owner
  // column revealed before the names land widens after first paint (D-456).
  const ownerNamesSettled = useRefNamesSettled(
    memberRef.kind,
    rows.flatMap((row) =>
      owners.byId.get(row.owner_user_id) === undefined ? [row.owner_user_id] : [],
    ),
  );
  const ready =
    refused || (views.settled && (listSettled || paused) && (ownerNamesSettled || paused));

  // Module 06's chip rule, and the reason this is a length test rather than a
  // permission one: a single-facility tenant is offered NO control instead of
  // a select with one option and nothing to choose. The service narrows every
  // read to the caller's facility scope anyway, so "all" is not a wider read.
  // A facility the address already holds keeps the control, so the filter in
  // force is visible and can be cleared (a view saved on a larger tenant, or
  // a pasted link).
  const offerFacility = facilityItems.length > 1 || search.facility !== undefined;

  const chipLabel = (filter: string, value: string): string =>
    t("sales.common.filterChip", { filter, value });

  return (
    <section className="sales-orders-screen" data-testid="sales-orders-screen">
      <PageHeader
        title={t("sales.orders.title")}
        actions={
          <>
            <div className="sales-toolbar">
              {/* Offered once the first page has rows; until then the same control
              holds its place, inert and invisible, so the head never moves when
              the rows arrive (W2, PR-E4 wave 5). */}
              {refused ? null : rows.length > 0 ? (
                <SalesExportButton exporter={exporter} />
              ) : (
                <SalesExportSlot />
              )}
              {/* §4.19: open orders loaded from a file, for an importer of the lane
              (spec 02 §7). The page itself gates again. */}
              {mayImportOrders ? (
                <Button
                  data-testid="sales-orders-import"
                  onClick={() => {
                    void navigate({ to: "/sales/orders/import" });
                  }}
                >
                  {t("sales.orderImport.open")}
                </Button>
              ) : null}
              {/* §4.7's "New order" → the §4.4 composer in order mode (P3-D PR-D1,
              D-283). Offered on `sales.order.write`, the code `POST /v1/orders`
              checks — the customer book's own idiom, so a planner or finance
              reader who can open the book is not offered a verb that 403s
              (D-088). */}
              {perms.has("sales.order.write") ? (
                <Button
                  variant="primary"
                  data-testid="sales-order-new"
                  onClick={() => {
                    void navigate({ to: "/sales/orders/new" });
                  }}
                >
                  {t("sales.orders.new")}
                </Button>
              ) : null}
            </div>
          </>
        }
      />

      <SalesExportNotes exporter={exporter} />

      <SalesFilterBar
        data-testid="sales-orders-filters"
        onClearAll={() => {
          views.write({ ...search, status: undefined, facility: undefined, at_risk: undefined });
        }}
        filters={[
          {
            key: "status",
            inline: true,
            applied:
              search.status === undefined
                ? undefined
                : chipLabel(
                    t("sales.orders.filterStatus"),
                    t(`sales.orders.status.${search.status}`),
                  ),
            onClear: () => {
              views.write({ ...search, status: undefined });
            },
            control: (
              <Field label={t("sales.orders.filterStatus")}>
                <Select
                  value={search.status ?? ""}
                  data-testid="orders-status"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      status: ORDER_STATUSES.find((status) => status === e.target.value),
                    });
                  }}
                >
                  <option value="">{t("sales.orders.filterAll")}</option>
                  {ORDER_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {t(`sales.orders.status.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
          {
            key: "at_risk",
            inline: true,
            applied: search.at_risk === undefined ? undefined : t("sales.orders.filterAtRisk"),
            onClear: () => {
              views.write({ ...search, at_risk: undefined });
            },
            control: (
              <CheckboxField
                label={t("sales.orders.filterAtRisk")}
                checked={search.at_risk !== undefined}
                data-testid="orders-at-risk"
                onChange={(checked) => {
                  views.write({ ...search, at_risk: checked ? "1" : undefined });
                }}
              />
            ),
          },
          {
            key: "customer",
            inline: true,
            control: (
              <>
                <Field label={t("sales.orders.filterCustomer")}>
                  <input
                    value={customerText}
                    placeholder={t("sales.orders.filterCustomerHint")}
                    data-testid="orders-customer-q"
                    onChange={(e) => {
                      setCustomerText(e.target.value);
                      if (customerId !== undefined) writeCustomer(undefined);
                    }}
                  />
                </Field>
                {customerQ.length > 1 ? (
                  <Field label={t("sales.orders.filterCustomer")}>
                    <Select
                      value={customerId ?? ""}
                      data-testid="orders-customer"
                      onChange={(e) => {
                        writeCustomer(e.target.value.length > 0 ? e.target.value : undefined);
                      }}
                    >
                      <option value="">{t("sales.orders.filterAll")}</option>
                      {(customerMatches.data?.items ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.display_name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
              </>
            ),
          },
          {
            key: "facility",
            applied:
              search.facility === undefined
                ? undefined
                : chipLabel(
                    t("sales.orders.filterFacility"),
                    facilityName ?? t("sales.common.notListed"),
                  ),
            onClear: () => {
              views.write({ ...search, facility: undefined });
            },
            control: offerFacility ? (
              <Field label={t("sales.orders.filterFacility")}>
                <Select
                  value={search.facility ?? ""}
                  data-testid="orders-facility"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      facility: e.target.value.length > 0 ? e.target.value : undefined,
                    });
                  }}
                >
                  <option value="">{t("sales.orders.filterAll")}</option>
                  {search.facility !== undefined && facilityName === undefined ? (
                    <option value={search.facility}>
                      {facilities.isPending ? t("ref.resolving") : t("sales.common.notListed")}
                    </option>
                  ) : null}
                  {facilityItems.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null,
          },
        ]}
      />

      <ViewsRegion ready={ready} cols={columns.length}>
        {!refused ? <ViewSwitcher views={views} /> : null}

        {customerId !== undefined ? (
          // The filter said out loud, so an arrival param is something the
          // reader can SEE and clear. `sales-toolbar` is the register's flex row
          // and `sales-hint` its muted ink — no new class for one chip.
          <div
            className="sales-toolbar"
            data-testid="sales-orders-customer-chip"
            data-customer={customerId}
          >
            <span className="sales-hint">{t("sales.orders.filterCustomer")}</span>
            {/* The same four-state reference the quote book's chip uses: in
                flight, refused, unknown, or a name — never a uuid standing in
                for one. */}
            <span data-testid="sales-orders-customer-name">
              <RefLabel
                value={filteredCustomerName}
                id={customerId}
                pending={filteredCustomer.isPending}
              />
            </span>
            <Button
              size="sm"
              data-testid="sales-orders-customer-clear"
              onClick={clearCustomerFilter}
            >
              {t("sales.common.filterCustomerClear")}
            </Button>
          </div>
        ) : null}

        {/* A refused filter has no file to name; the empty state below says
            why and carries the way back. */}
        {search.import_job !== undefined && !importRefused ? (
          <div
            className="sales-toolbar"
            data-testid="sales-orders-import-chip"
            data-import={search.import_job}
          >
            <span className="sales-hint">{t("sales.orders.filterImport")}</span>
            <Link
              to="/sales/orders/import/$importId"
              params={{ importId: search.import_job }}
              data-testid="sales-orders-import-name"
            >
              <RefLabel
                value={importJob.data?.file_name}
                id={search.import_job}
                pending={importJob.isPending}
              />
            </Link>
            <Button size="sm" data-testid="sales-orders-import-clear" onClick={clearImportFilter}>
              {t("sales.orders.filterImportClear")}
            </Button>
          </div>
        ) : null}

        {refused ? (
          <ForbiddenState title={t("sales.access.title")} body={t("sales.access.orders")} />
        ) : null}

        {!refused && importRefused ? (
          <EmptyState
            title={t("sales.orders.importRefusedTitle")}
            body={t("sales.orders.importRefusedBody")}
            ctaLabel={t("sales.orders.filterImportClear")}
            onCta={clearImportFilter}
          />
        ) : null}

        {!refused && paused && rows.length === 0 ? (
          <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
        ) : null}

        {!refused && page.isPending && !paused && rows.length === 0 ? (
          <TableSkeleton rows={5} cols={columns.length} label={t("sales.orders.loading")} />
        ) : null}

        {!refused &&
        page.error !== null &&
        !(page.error instanceof UnauthorizedError) &&
        !valueOrderRefused &&
        !importRefused ? (
          <ErrorState
            title={t("sales.orders.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(page.error)}
            onRetry={() => {
              void page.refetch();
            }}
          />
        ) : null}

        {!refused && !page.isPending && !paused && page.error === null && rows.length === 0 ? (
          // Two empties, because they ask for different acts: an empty BOOK is
          // §4.7's own sentence about where orders come from; an empty FILTER is
          // "widen it". Telling a reader with a customer filter on that orders
          // appear when quotes are accepted is an answer to a question they did
          // not ask — and false about the tenant, which has orders.
          // The at-risk toggle alone answering nothing is good news, and it
          // reads as such rather than as a filter to widen.
          isFiltered && search.at_risk !== undefined && !isFilteredBeyondRisk ? (
            <EmptyState
              title={t("sales.orders.atRiskEmptyTitle")}
              body={t("sales.orders.atRiskEmptyBody")}
            />
          ) : isFiltered ? (
            <EmptyState
              title={t("sales.orders.emptyFilteredTitle")}
              body={t("sales.orders.emptyFilteredBody")}
            />
          ) : (
            <EmptyState title={t("sales.orders.emptyTitle")} body={t("sales.orders.emptyBody")} />
          )
        ) : null}

        {rows.length > 0 ? (
          <>
            <DataTable
              pager={bookPager(t, i18n.language, pageView, {
                turning,
                onNext: () => {
                  const next = shownData?.next_cursor;
                  if (next === null || next === undefined) return;
                  pages.next(next, rows.length);
                },
                onPrev: () => {
                  pages.prev();
                },
              })}
              data-testid="sales-orders-table"
              fit
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              empty={null}
              caption={t("sales.orders.tableCaption")}
              rowTestId="sales-order-row"
              rowProps={(row) => ({
                "data-status": row.status,
                "data-credit": row.credit_check_status,
              })}
              sticky
              keyboardNav
              columnConfig={views.columnConfig}
              sort={{
                value: order,
                onChange: setOrder,
                toggleLabel: (header: string, next: "ascending" | "descending") =>
                  next === "descending"
                    ? t("sales.orders.sortDesc", { column: header })
                    : t("sales.orders.sortAsc", { column: header }),
              }}
              peek={{
                key: (row) => peekCode("order", row.id),
                current: peek.code,
                onOpen: (code) => {
                  peek.open(code);
                },
                onClose: peek.close,
                label: t("sales.peek.open"),
                rowLabel: (row) => t("sales.peek.openNamed", { name: row.order_no }),
              }}
              onRowClick={(row) => {
                void navigate({ to: "/sales/orders/$orderId", params: { orderId: row.id } });
              }}
            />
          </>
        ) : null}
      </ViewsRegion>

      <SalesPeek peek={peek} />
    </section>
  );
}
